import { DefaultTranslationFieldsInTable, DisplayInOptionsEnum, allElements } from "constants/constants";
import { LocationsEntity } from "entities/Locations/LocationsEntity";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ISummaryReportDashboardFilters, IOption } from "./SummaryReportDashboardFiltersInterfaces";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";
import { objectComparator } from "utilities/utilities";

export const initialSummaryReportDashboardFilters: ISummaryReportDashboardFilters = {
    productionLine: allElements,
    machineCenter: [allElements],
    functionalLocation: allElements,
    shift: allElements,
    failureTypes: [allElements],
};

export const validMillSummaryDefaultFields = ["machineCenter", "functionalLocation", "shift", "failureTypes"];

const useSummaryReportDashboardFilters = () => {
    const {
        handleFiltersChange: handleSetContextFilters,
        filters: filtersContext,
        handleShowAllProductionLinesChange,
        handleSelectedProductionLineChange,
        showAllProductionLines,
        resetSelections,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        userFiltersDefault,
        loadingDefaultOperations,
    } = useContext(SummaryReportDashboardContext);

    const { reportRangeEndDate: endDate, reportRangeStartDate: startDate } = useContext(MillDashboardContext);

    const { millId } = useParams();
    const [filters, setFilters] = useState<ISummaryReportDashboardFilters>(filtersContext);
    const { summaryReportDashboardFiltersData, loading } = LocationsEntity.GetSummaryReportDashboardFilters(
        Number(millId),
        {
            ...filters,
            productionLine: filtersContext.productionLine,
            startDate,
            endDate,
        },
    );

    const isLoading = loading;
    const { t, i18n } = useTranslation();

    const displayInOptions = useMemo(
        () => [
            { id: DisplayInOptionsEnum.PERCENT, name: t("corporate_dashboard.filters.display_in.percent") },
            { id: DisplayInOptionsEnum.MINUTES, name: t("corporate_dashboard.filters.display_in.minutes") },
            { id: DisplayInOptionsEnum.OCCURRENCES, name: t("corporate_dashboard.filters.display_in.occurrences") },
        ],
        [t],
    );

    const allLabel = useMemo(() => t("locations.summary_report.filters.all"), [t]);

    const functionalLocationsOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const functionalLocationsList =
            summaryReportDashboardFiltersData?.functional_location
                ?.filter((functionalLocation) =>
                    filters.machineCenter?.length! > 0 &&
                    !filters.machineCenter?.includes(allElements) &&
                    Array.isArray(filters.machineCenter)
                        ? filters.machineCenter?.find((el) => el === functionalLocation.area.machine_center_id)
                        : true,
                )
                .map((functionalLocation) => {
                    return {
                        id: functionalLocation.functional_location_id,
                        name: handleNameTranslation(i18n.language, functionalLocation.translation.translation_names),
                    } as IOption;
                }) || [];

        functionalLocationsList.unshift({ id: allElements, name: allLabel });
        return functionalLocationsList;
    }, [
        allLabel,
        loading,
        summaryReportDashboardFiltersData?.functional_location,
        filters.machineCenter,
        i18n.language,
    ]);

    const machineCentersOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const machineCentersList =
            summaryReportDashboardFiltersData?.machine_center?.map((machineCenter) => {
                return {
                    id: machineCenter.machine_center_id,
                    name: machineCenter.name,
                } as IOption;
            }) || [];

        machineCentersList.unshift({ id: allElements, name: allLabel });
        return machineCentersList;
    }, [allLabel, loading, summaryReportDashboardFiltersData?.machine_center]);

    const shiftsOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const shiftsList =
            summaryReportDashboardFiltersData?.shift
                ?.filter((shift) =>
                    filters.machineCenter?.length! > 0 &&
                    !filters.machineCenter?.includes(allElements) &&
                    Array.isArray(filters.machineCenter)
                        ? filters.machineCenter?.find((el) =>
                              shift.machine_centers.find((machineCenterId) => machineCenterId.machine_center_id === el),
                          )
                        : true,
                )
                .filter((shift) =>
                    filters.functionalLocation !== allElements
                        ? shift.functional_locations.find(
                              (el) => el.functional_location_id === filters.functionalLocation,
                          )
                        : true,
                )
                .map((shift) => {
                    return {
                        id: shift.shift_id,
                        name: shift.name,
                    } as IOption;
                }) || [];

        shiftsList.unshift({ id: allElements, name: allLabel });
        return shiftsList;
    }, [
        allLabel,
        loading,
        summaryReportDashboardFiltersData?.shift,
        filters.machineCenter,
        filters.functionalLocation,
    ]);

    const failureTypesOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const failureTypesList =
            summaryReportDashboardFiltersData?.failure_type
                ?.filter((failureType) =>
                    filters.machineCenter?.length! > 0 &&
                    !filters.machineCenter?.includes(allElements) &&
                    Array.isArray(filters.machineCenter)
                        ? filters.machineCenter?.find((el) =>
                              failureType.machine_centers.find(
                                  (machineCenterId) => machineCenterId.machine_center_id === el,
                              ),
                          )
                        : true,
                )
                .filter((failureType) =>
                    filters.functionalLocation !== allElements
                        ? failureType.functional_locations.find(
                              (el) => el.functional_location_id === filters.functionalLocation,
                          )
                        : true,
                )
                .filter((failureType) =>
                    filters.shift !== allElements
                        ? failureType.shifts.find((el) => el.shift_id === filters.shift)
                        : true,
                )
                .map((failureType) => {
                    const defaultFailureTypeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureType,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_TYPE,
                    );
                    return {
                        id: failureType.failure_type_id,
                        name: defaultFailureTypeTranslatedName,
                    } as IOption;
                }) || [];

        failureTypesList?.unshift({ id: allElements, name: allLabel });
        return failureTypesList;
    }, [
        loading,
        allLabel,
        summaryReportDashboardFiltersData?.failure_type,
        filters.machineCenter,
        filters.functionalLocation,
        filters.shift,
        i18n.language,
    ]);

    const handleFiltersChange = useCallback(
        (newFilters: ISummaryReportDashboardFilters) => {
            if (Array.isArray(newFilters.failureTypes)) {
                if (newFilters.failureTypes.length <= 1) {
                    newFilters.failureTypes = [allElements];
                } else if (newFilters.failureTypes.length > 0 && newFilters.failureTypes.indexOf(allElements) === 0) {
                    newFilters.failureTypes = newFilters.failureTypes.filter((failureType) => {
                        return failureType !== allElements;
                    });
                } else if (newFilters.failureTypes.length > 1 && newFilters.failureTypes.includes(allElements)) {
                    newFilters.failureTypes = newFilters.failureTypes.filter((failureType) => {
                        return failureType === allElements;
                    });
                }
            }
            if (Array.isArray(newFilters.machineCenter)) {
                if (newFilters.machineCenter.length <= 1) {
                    newFilters.machineCenter = [allElements];
                } else if (newFilters.machineCenter.length > 0 && newFilters.machineCenter.indexOf(allElements) === 0) {
                    newFilters.machineCenter = newFilters.machineCenter.filter((machineCenter) => {
                        return machineCenter !== allElements;
                    });
                } else if (newFilters.machineCenter.length > 1 && newFilters.machineCenter.includes(allElements)) {
                    newFilters.machineCenter = newFilters.machineCenter.filter((machineCenter) => {
                        return machineCenter === allElements;
                    });
                }
            }

            setFilters({ ...filters, ...newFilters });
        },
        [filters],
    );

    const applyFilters = () => {
        handleSetContextFilters({ ...filters, startDate, endDate });
        resetSelections();
    };

    const resetFilters = () => {
        setFilters({
            ...initialSummaryReportDashboardFilters,
            startDate,
            endDate,
        });
        handleSetContextFilters({
            ...initialSummaryReportDashboardFilters,
            startDate,
            endDate,
        });
        handleShowAllProductionLinesChange(true);
        !showAllProductionLines && handleSelectedProductionLineChange(allElements, "");
    };

    const setDefaultFilters = async () => {
        try {
            await handleSetFiltersDefault(filters);
            handleSetContextFilters({ ...filters, startDate, endDate });
        } catch (error) {
            console.error(error);
        }
    };

    const removeDefaultFilters = async () => {
        try {
            await handleRemoveDefaultFilters();
            resetFilters();
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (filtersContext.productionLine) {
            setFilters({
                ...filtersContext,
                startDate,
                endDate,
            });
        }
    }, [filtersContext, setFilters, startDate, endDate]);

    const isFiltersDefault = useMemo(() => {
        if (userFiltersDefault) {
            return objectComparator(userFiltersDefault, filters, validMillSummaryDefaultFields);
        }

        return false;
    }, [userFiltersDefault, filters]);

    return {
        isLoading,
        machineCentersOptions,
        functionalLocationsOptions,
        shiftsOptions,
        displayInOptions,
        failureTypesOptions,
        applyFilters,
        resetFilters,
        filters,
        handleFiltersChange,
        setDefaultFilters,
        removeDefaultFilters,
        isFiltersDefault,
        userFiltersDefault,
        loadingDefaultOperations,
    };
};

export default useSummaryReportDashboardFilters;
